import cn from 'classnames';
import { Drawer, Form, Localization, Responsive } from 'connex-cds';
import { filter, map, sortBy, find } from 'lodash';
import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useViewport } from '../../../../../../util/useViewport';
import { useVehicleTypeSetup, useVehicleSetup } from '../../MasterDataProvider';
import style from '../../style';
import { useTicketContext } from '../../TicketContext';
import EditStatusTime from './EditStatusTime';
import { StatusTimesDesktop } from './StatusTimesDesktop';
import { StatusTimesMobile } from './StatusTimesMobile';

const { useTranslateObjects } = Localization;
const { useDrawerContext } = Drawer;

const Styled = styled.div`
  ${style}
`;

const PUMP_STATUS_DEFS = [
  { eventType: 'PRINTED', getConfig: vt => vt?.deliveriesConfiguration?.pump?.ticketed },
  { eventType: 'TO_JOB', getConfig: vt => vt?.deliveriesConfiguration?.pump?.toJob },
  { eventType: 'ARRIVE_JOB', getConfig: vt => vt?.deliveriesConfiguration?.pump?.arriveJob },
  { eventType: 'PUMPING_STARTED', getConfig: vt => vt?.deliveriesConfiguration?.pump?.startPump },
  { eventType: 'PUMPING_COMPLETE', getConfig: vt => vt?.deliveriesConfiguration?.pump?.finishPump },
  { eventType: 'READY_TO_PUMP', getConfig: vt => vt?.deliveriesConfiguration?.pump?.readyToPump },
  { eventType: 'LEAVE_JOB', getConfig: vt => vt?.deliveriesConfiguration?.pump?.departJob },
  { eventType: 'IN_YARD', getConfig: vt => vt?.deliveriesConfiguration?.pump?.arrivePlant },
];

const AGGREGATE_STATUS_DEFS = [
  { eventType: 'PRINTED', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.ticketed },
  { eventType: 'LOADING_STARTED', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.loading },
  { eventType: 'LOADING_COMPLETE', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.loaded },
  { eventType: 'TO_JOB', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.toJob },
  { eventType: 'ARRIVE_JOB', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.arriveJob },
  { eventType: 'UNLOADING', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.beginUnload },
  { eventType: 'END_UNLOADING', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.endUnload },
  { eventType: 'LEAVE_JOB', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.departJob },
  { eventType: 'IN_YARD', getConfig: vt => vt?.deliveriesConfiguration?.aggregate?.arrivePlant },
];

const READY_MIX_STATUS_DEFS = [
  { eventType: 'PRINTED', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.ticketed },
  { eventType: 'LOADING_STARTED', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.loading },
  { eventType: 'LOADING_COMPLETE', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.loaded },
  { eventType: 'TO_JOB', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.toJob },
  { eventType: 'ARRIVE_JOB', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.arriveJob },
  { eventType: 'UNLOADING', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.beginUnload },
  { eventType: 'END_UNLOADING', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.endUnload },
  { eventType: 'LEAVE_JOB', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.departJob },
  { eventType: 'IN_YARD', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.arrivePlant },
  { eventType: 'WASHING', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.beginWash },
  { eventType: 'END_WASHING', getConfig: vt => vt?.deliveriesConfiguration?.readyMix?.endWash },
];

const TICKET_STATUS_ORDER = [
  'CREATED',
  'PRINTED',
  'LOADING_STARTED',
  'LOADING_COMPLETE',
  'FULLY_MIXED',
  'TO_JOB',
  'ARRIVE_JOB',
  'READY_TO_PUMP',
  'UNLOADING',
  'PUMPING_STARTED',
  'END_UNLOADING',
  'WASHING',
  'END_WASHING',
  'LEAVE_JOB',
  'ARRIVE_PLANT',
  'IN_YARD',
];

const getDeliveryStatusDefs = (ticketType, systemProductType) => {
  if (ticketType === 'PMP') {
    return PUMP_STATUS_DEFS;
  }
  if (systemProductType === 'AGGREGATE') {
    return AGGREGATE_STATUS_DEFS;
  }
  return READY_MIX_STATUS_DEFS;
};

const isStatusVisible = config => config?.visible !== false;

const isStatusEditable = config => config?.editable === true;

const isEventVisible = (event, vehicleTypeData, statusDefs) => {
  const def = statusDefs.find(d => d.eventType === event?.eventType);
  if (!def) {
    return false;
  }
  return isStatusVisible(def.getConfig(vehicleTypeData));
};

const isEventEditable = (eventType, vehicleTypeData, statusDefs) => {
  const def = statusDefs.find(d => d.eventType === eventType);
  if (!def) {
    return false;
  }
  return isStatusEditable(def.getConfig(vehicleTypeData));
};

const hasOccurredOnTicket = (eventType, ticketEventsOnTicket) => !!ticketEventsOnTicket?.[eventType]?.eventDateTime;

const getStandalonePlaceholderEvents = (existingEvents, vehicleTypeData, statusDefs) => {
  const existingTypes = new Set(existingEvents.map(e => e.eventType));
  return statusDefs
    .filter(def => {
      const config = def.getConfig(vehicleTypeData);
      return isStatusVisible(config) && isStatusEditable(config) && !existingTypes.has(def.eventType);
    })
    .map(def => ({ eventType: def.eventType }));
};

const StatusTimes = () => {
  const { ticket, refreshTicket, refreshing, isStandAloneTicket } = useTicketContext();
  const { values } = Form.useFormContext();
  const vehicleTypeSetup = useVehicleTypeSetup();
  const vehicleSetup = useVehicleSetup();

  const primaryLineItem = useMemo(() => find(ticket?.lineItems, { isPrimary: true }), [ticket?.lineItems]);

  const vehicleTypeData = useMemo(() => {
    const targetVehicle = find(vehicleSetup.data, { id: ticket?.vehicleId });
    return find(vehicleTypeSetup.data, { crn: targetVehicle?.vehicleType?.vehicleTypeRef });
  }, [vehicleSetup.data, vehicleTypeSetup.data, ticket?.vehicleId]);

  const statusDefs = useMemo(
    () => getDeliveryStatusDefs(ticket?.customData?.ticketType, primaryLineItem?.productType?.systemProductType),
    [ticket?.customData?.ticketType, primaryLineItem?.productType?.systemProductType]
  );

  const ticketEvents = useMemo(() => {
    const mergedTicketEvents = { ...ticket?.ticketEvents, ...values?.ticketEvents };
    return map(mergedTicketEvents, (value, key) => ({ eventType: key, ...value }));
  }, [ticket?.ticketEvents, values?.ticketEvents]);

  const filteredEvents = useMemo(() => {
    const fromTicket = filter(ticketEvents, event => {
      if (!isEventVisible(event, vehicleTypeData, statusDefs)) {
        return false;
      }

      return hasOccurredOnTicket(event.eventType, ticket?.ticketEvents);
    });

    if (isStandAloneTicket && ticket?.crn && vehicleTypeData) {
      return [...fromTicket, ...getStandalonePlaceholderEvents(fromTicket, vehicleTypeData, statusDefs)];
    }

    return fromTicket;
  }, [ticketEvents, ticket?.ticketEvents, ticket?.crn, vehicleTypeData, statusDefs, isStandAloneTicket]);

  const wCContact = useMemo(() => {
    if (
      ticket?.customData?.productDetails?.wcContactDateTime &&
      !(vehicleTypeData?.deliveriesConfiguration?.readyMix?.firstWaterCementContact?.visible === false)
    ) {
      return {
        eventType: 'FIRST_WCCONTACT',
        eventDateTime: ticket?.customData?.productDetails?.wcContactDateTime,
      };
    }
    return null;
  }, [ticket, vehicleTypeData?.deliveriesConfiguration?.readyMix?.firstWaterCementContact?.visible]);

  const updatedEvents = useMemo(() => {
    if (wCContact) {
      return [...filteredEvents, wCContact];
    }
    return filteredEvents;
  }, [wCContact, filteredEvents]);

  const orderIndexByEventType = useMemo(
    () =>
      isStandAloneTicket
        ? Object.fromEntries(TICKET_STATUS_ORDER.map((eventType, index) => [eventType, index]))
        : Object.fromEntries(statusDefs.map((def, index) => [def.eventType, index])),
    [isStandAloneTicket, statusDefs]
  );

  const sortedEvents = useMemo(() => {
    return sortBy(updatedEvents, event => {
      const orderIndex = orderIndexByEventType?.[event?.eventType] ?? Number.MAX_SAFE_INTEGER;
      if (isStandAloneTicket) {
        return orderIndex;
      }
      const eventDateTime = values?.ticketEvents?.[event?.eventType]?.eventDateTime || event?.eventDateTime;
      return [eventDateTime ?? Number.POSITIVE_INFINITY, orderIndex];
    });
  }, [isStandAloneTicket, updatedEvents, values, orderIndexByEventType]);

  const { translateObjects } = useTranslateObjects();
  const translatedTicketEvents = useMemo(() => {
    return translateObjects(sortedEvents, {
      getPath: 'eventType',
      setPath: 'eventTypeTranslated',
    });
  }, [sortedEvents, translateObjects]);

  const { openDrawer } = useDrawerContext();

  const dimensions = useViewport();

  const handleRowEdit = useCallback(
    row => {
      openDrawer({
        titleStringId: 'updateStatusTime',
        width: Math.min(500, dimensions?.width),
        component: <EditStatusTime statusType={row?.eventType} />,
      });
    },
    [dimensions?.width, openDrawer]
  );

  const isRowEditable = useCallback(
    row => isEventEditable(row?.eventType, vehicleTypeData, statusDefs),
    [vehicleTypeData, statusDefs]
  );

  const handleRefreshClick = useCallback(() => {
    refreshTicket();
  }, [refreshTicket]);

  return (
    <Styled className={cn('ticket-events', 'mt-container')}>
      <Responsive>
        <StatusTimesMobile
          ticketEvents={translatedTicketEvents}
          isRowEditable={isRowEditable}
          handleRowEdit={handleRowEdit}
          handleRefreshClick={handleRefreshClick}
          refreshing={refreshing}
        />
        <StatusTimesDesktop
          ticketEvents={translatedTicketEvents}
          isRowEditable={isRowEditable}
          handleRowEdit={handleRowEdit}
          handleRefreshClick={handleRefreshClick}
          refreshing={refreshing}
        />
      </Responsive>
    </Styled>
  );
};

export default StatusTimes;
